import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import styles from '../Video/VideoContainer.module.scss';
import { useTranslation } from 'react-i18next';

type PortalGuideFormProps = {
  documents: vp.PortalDocument[];
  filteredByKeyWordLists: { [key: string]: vp.PortalDocument };
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  setFilteredByKeyWordLists: (doc: { [key: string]: vp.PortalDocument }) => void;
  getSelectedDocument: (doc: vp.PortalDocument) => void;
  setHasSearchResult: (e: boolean) => void;
};

type FC = FunctionComponent<PortalGuideFormProps>;

const { TextField, Button, Icon } = vp;

const PortalGuideForm: FC = (props: PortalGuideFormProps) => {
  const {
    documents,
    filteredByKeyWordLists,
    searchTerm,
    setSearchTerm,
    setFilteredByKeyWordLists,
    getSelectedDocument,
    setHasSearchResult,
  } = props;
  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const { t } = useTranslation('translations', { keyPrefix: 'LearningCentre.PDFGuides' });

  useEffect(() => {
    if (searchTerm === '') {
      setFilteredByKeyWordLists({});
    } else {
      const filtered = (documents || [])
        .filter((doc) => doc.title.toLowerCase().includes(searchTerm.toLowerCase()))
        .reduce(
          (accumulator, doc, index) => ({
            ...accumulator,
            [index]: { ...doc },
          }),
          {},
        );
      setFilteredByKeyWordLists(filtered);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (searchTerm === '') {
      setHasSearchResult(false);
    }
  }, [searchTerm]);

  useEffect(() => {
    if (Object.values(filteredByKeyWordLists || {}).length !== 0) {
      setIsMenuVisible(true);
    } else {
      setIsMenuVisible(false);
    }
  }, [filteredByKeyWordLists]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const button = document.getElementById('search-tutorial-btn');
      button?.click();
    }
  };

  return (
    <div className={styles.searchContainer}>
      <div className={styles.searchInput}>
        <TextField
          name="docu-search"
          elementLeft={<Icon icon="search" color="primary-dark-transparent" width="18px" height="18px" />}
          value={searchTerm}
          onBlur={() => {}}
          onKeyPress={handleKeyPress}
          onChange={(e) => {
            setSearchTerm(e.target.value);
          }}
        />
        {isMenuVisible && (
          <div className={styles.listContainer}>
            {Object.values(filteredByKeyWordLists).map((doc, index) => {
              const title = doc.title;
              const lowerSearchTerm = searchTerm.toLowerCase();
              const indexOfMatch = title.toLowerCase().indexOf(lowerSearchTerm);

              if (indexOfMatch !== -1) {
                const beforeMatch = title.substring(0, indexOfMatch);
                const match = title.substring(indexOfMatch, indexOfMatch + lowerSearchTerm.length);
                const afterMatch = title.substring(indexOfMatch + lowerSearchTerm.length);
                return (
                  <div
                    key={index}
                    className={styles.selectOption}
                    onClick={() => {
                      getSelectedDocument(doc);
                      setIsMenuVisible(false);
                    }}
                  >
                    {beforeMatch}
                    <strong>{match}</strong>
                    {afterMatch}
                  </div>
                );
              } else {
                return (
                  <div
                    key={index}
                    className={styles.selectOption}
                    onClick={() => {
                      getSelectedDocument(doc);
                      setIsMenuVisible(false);
                    }}
                  >
                    {title}
                  </div>
                );
              }
            })}
          </div>
        )}
      </div>
      <Button
        id="search-tutorial-btn"
        label={t('searchButton')}
        width="78px"
        height="40px"
        color="yellow"
        type="submit"
        onClick={() => {
          setHasSearchResult(true);
          setIsMenuVisible(false);
        }}
      />
    </div>
  );
};

export default PortalGuideForm;
